"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMissingDependentValuesets = exports.parseTimeStringAsUTCConvertingToEndOfYear = exports.parseTimeStringAsUTC = exports.valueSetsForCodeService = void 0;
var moment_1 = __importDefault(require("moment"));
var CustomErrors_1 = require("../types/errors/CustomErrors");
/**
 * Create the code service valueset database that the cql-execution engine needs.
 *
 * NOTE: This uses the `compose` attribue of the ValueSet to get code. This is incorrect and
 * should be using the `expansion`. But current example measures have ValueSets with compose
 * only.
 *
 * @param valueSetResources FHIR ValueSets.
 * @returns The value set DB structure needed for the cql-execution CodeService.
 */
function valueSetsForCodeService(valueSetResources) {
    var valueSets = {};
    var valueSetId;
    var version;
    valueSetResources.forEach(function (valueSet) {
        if (valueSet.url) {
            // Grab id for this valueset (should match FHIR ValueSet url)
            valueSetId = valueSet.url;
            if (!valueSets[valueSetId]) {
                valueSets[valueSetId] = {};
            }
            // Grab ValueSet version. This usually is not used.
            version = valueSet.version || '';
            if (version === 'N/A') {
                version = '';
            }
            // Create array for valueset members.
            if (!valueSets[valueSetId][version]) {
                valueSets[valueSetId][version] = [];
            }
        }
        else {
            // TODO: handle situation where ValueSet does not have URL
        }
        if (valueSet.expansion && valueSet.expansion.contains && valueSet.expansion.contains.length > 0) {
            // Default to using expansion if it exists
            valueSets[valueSetId][version] = getHierarchicalCodes(valueSet.expansion.contains);
        }
        else if (valueSet.compose) {
            // Only use compose if expansion doesn't exist
            // Iterate over include components and add all concepts
            valueSet.compose.include.forEach(function (include) {
                var _a;
                (_a = include.concept) === null || _a === void 0 ? void 0 : _a.forEach(function (concept) {
                    if (concept.code && include.system) {
                        valueSets[valueSetId][version].push({
                            code: concept.code,
                            system: include.system,
                            version: include.version,
                            display: concept.display
                        });
                    }
                });
            });
        }
        else {
            // TODO: Handle situation when ValueSet does not have expansion or compose.
        }
    });
    return valueSets;
}
exports.valueSetsForCodeService = valueSetsForCodeService;
function getHierarchicalCodes(contains) {
    var codes = [];
    contains.forEach(function (contain) {
        if (!contain.abstract && !contain.inactive && contain.code && contain.system) {
            codes.push({
                code: contain.code,
                system: contain.system,
                version: contain.version,
                display: contain.display
            });
        }
        if (contain.contains && contain.contains.length > 0) {
            codes.push.apply(codes, getHierarchicalCodes(contain.contains));
        }
    });
    return codes;
}
// Create Date from UTC string date and time using momentJS
function parseTimeStringAsUTC(timeValue) {
    return moment_1.default.utc(timeValue, 'YYYYMDDHHmm').toDate();
}
exports.parseTimeStringAsUTC = parseTimeStringAsUTC;
// Create Date from UTC string date and time using momentJS, shifting to 11:59:59 of the given year
function parseTimeStringAsUTCConvertingToEndOfYear(timeValue) {
    return moment_1.default.utc(timeValue, 'YYYYMDDHHmm').add(1, 'years').subtract(1, 'seconds').toDate();
}
exports.parseTimeStringAsUTCConvertingToEndOfYear = parseTimeStringAsUTCConvertingToEndOfYear;
/**
 * Collates dependent valuesets from a measure by going through all of the measure bundle's libraries' dataCriteria's codeFilters' valueset.
 * Then finds all valuesets that are not already contained in the measure bundle.
 *
 * @param {fhir4.Bundle} measureBundle - A measure bundle object that contains all libraries and valuesets used by the measure
 * @param {fhir4.ValueSet[]} valueSetCache - Cache of existing valueset objects on disk to include in lookup
 * @returns {string[]} An array of all dependent valueset urls in the measure that are used by the measure's libraries but not contained in the measure bundle
 */
function getMissingDependentValuesets(measureBundle, valueSetCache) {
    var _a;
    if (valueSetCache === void 0) { valueSetCache = []; }
    if (!measureBundle.entry) {
        throw new CustomErrors_1.UnexpectedResource('Expected measure bundle to contain entries');
    }
    var libraryEntries = (_a = measureBundle.entry) === null || _a === void 0 ? void 0 : _a.filter(function (e) { var _a; return ((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) === 'Library' && e.resource.dataRequirement; });
    // create an array of valueset urls
    var vsURLs = libraryEntries.reduce(function (acc, lib) {
        var libraryResource = lib.resource;
        if (!libraryResource) {
            throw new CustomErrors_1.UnexpectedResource('Library entry not included in measure bundle');
        }
        else if (!libraryResource.dataRequirement || !(libraryResource.dataRequirement.length > 0)) {
            throw new CustomErrors_1.UnexpectedProperty('Expected library entry to have resource with dataRequirements that have codeFilters');
        }
        // pull all valuset urls out of this library's dataRequirements
        var libraryVSURL = libraryResource.dataRequirement.reduce(function (accumulator, dr) {
            if (dr.codeFilter && dr.codeFilter.length > 0) {
                // get each valueset url for each codeFilter (if valueset url exists)
                var vs = dr.codeFilter
                    .filter(function (cf) { return cf.valueSet; })
                    .map(function (cf) {
                    return cf.valueSet;
                });
                return accumulator.concat(vs);
            }
            else {
                return accumulator;
            }
        }, []);
        return acc.concat(libraryVSURL);
    }, []);
    // unique-ify
    var uniqueVS = vsURLs.filter(function (value, index, self) { return self.indexOf(value) === index; });
    // full array of all valueset URLs present across measureBundle and cache
    var existingValueSets = measureBundle.entry
        .filter(function (e) { var _a; return ((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) === 'ValueSet'; })
        .map(function (e) { return e.resource; })
        .concat(valueSetCache)
        .map(function (vs) { return vs.url; });
    // filter to any valueset urls that cannot be found
    return uniqueVS.filter(function (url) {
        return !existingValueSets.includes(url);
    });
}
exports.getMissingDependentValuesets = getMissingDependentValuesets;
//# sourceMappingURL=ValueSetHelper.js.map